import React, { useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import PaginatedTable from "../../shared/PaginatedTable";
import RewardRequestsTableActions from "./RewardRequestsTableActions";
import RewardRequestsTableFilters from "./RewardRequestsTableFilters";
import RewardRequestsTableHeader from "./RewardRequestsTableHeader";
import RewardRequestsTableRow from "./RewardRequestsTableRow";

import EduApi from "../../../services/resources/EduApi";
import { ApiCallErrorMessageHandler } from "../../../lib/coc-common-scripts";
import axios from "axios";
import ApproveRejectRewardRequestModal from "./ApproveRejectRewardRequestModal";

const actionTypes = {
  Approve: "approve",
  Reject: "reject",
};

export default function RewardRequestsTable(props) {
  const {
    eduChabadHouses,
    eduProgram,
    mobileMode,
    systemLists: { eduRewardRequestStatuses = [], eduRewardTypes = [] } = {},
    viewOnly,
  } = props;

  const [approveRejectRewardRequest, setApproveRejectRewardRequest] = useState({
    action: null,
    errorMessage: "",
    loading: false,
    rewardRequest: null,
  });
  const [filters, setFilters] = useState({
    keyword: "",
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rewardRequests, setRewardRequests] = useState([]);
  const [results, setResults] = useState(12);
  const [sortBy, setSortBy] = useState("createdOn");
  const [success, setSuccess] = useState(true);
  const [totalRewardRequests, setTotalRewardRequests] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [
    showApproveRejectRewardRequestConfirmationModal,
    setShowApproveRejectRewardRequestConfirmationModal,
  ] = useState(false);

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  useEffect(() => {
    unstable_batchedUpdates(() => {
      setLoading(true);
      setSuccess(true);
    });

    EduApi.getEduProgramRewardRequests(
      apiSignalRef.current.token,
      eduProgram.id,
      page,
      results,
      sortBy,
      filters,
    )
      .then(({ rewardRequests, totalRewardRequests }) => {
        unstable_batchedUpdates(() => {
          setLoading(false);
          setRewardRequests(rewardRequests);
          setTotalRewardRequests(totalRewardRequests);
        });
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          unstable_batchedUpdates(() => {
            setLoading(false);
            setRewardRequests([]);
            setSuccess(false);
            setTotalRewardRequests(0);
          });
        }
      });
  }, [eduProgram.id, filters, page, results, sortBy, refreshKey]);

  const filterRewardRequests = (newFilters) => {
    unstable_batchedUpdates(() => {
      setPage(1);
      setFilters(newFilters);
    });
  };

  const applySort = (newSortBy) => {
    unstable_batchedUpdates(() => {
      setPage(1);
      setSortBy(newSortBy);
    });
  };

  const loadData = (newPage, newResults, newSortBy) => {
    unstable_batchedUpdates(() => {
      setPage(newPage);
      if (newResults !== undefined) {
        setResults(newResults);
      }
      if (newSortBy !== undefined) {
        setSortBy(newSortBy);
      }
    });
  };

  const getRequestsForExport = async () => {
    const requestsForExport = await EduApi.getEduProgramRewardRequests(
      apiSignalRef.current.token,
      eduProgram.id,
      undefined,
      undefined,
      sortBy,
      filters,
      true,
    );

    return requestsForExport.rewardRequests;
  };

  const toggleApproveRejectRewardRequest = (rewardRequest, approve) => {
    setApproveRejectRewardRequest({
      action:
        approve === true
          ? actionTypes.Approve
          : approve === false
            ? actionTypes.Reject
            : null,
      errorMessage: "",
      loading: false,
      rewardRequest: rewardRequest || null,
    });
    setShowApproveRejectRewardRequestConfirmationModal((prev) => !prev);
  };

  const doApproveRejectRewardRequest = async () => {
    setApproveRejectRewardRequest({
      ...approveRejectRewardRequest,
      errorMessage: "",
      loading: true,
    });

    try {
      const { rewardRequest } = approveRejectRewardRequest;
      const updatedRewardRequest = {
        id: rewardRequest.id,
        eduRewardID: rewardRequest.eduRewardID,
        eduStudentEnrollmentID: rewardRequest.eduStudentEnrollmentID,
        //  BE only updates status field, no need to set other irrelevant fields for post
        status:
          approveRejectRewardRequest.action === actionTypes.Approve
            ? "Approved"
            : "Rejected",
      };

      await EduApi.submitEduStudentRewardRequest(
        apiSignalRef.current.token,
        rewardRequest.eduProgramID,
        updatedRewardRequest,
      );
    } catch (err) {
      setApproveRejectRewardRequest({
        ...approveRejectRewardRequest,
        errorMessage: ApiCallErrorMessageHandler(err),
        loading: false,
      });
      return;
    }

    setRefreshKey((k) => k + 1);
    toggleApproveRejectRewardRequest();
  };

  const isApproving = approveRejectRewardRequest.action === actionTypes.Approve;
  const isRejecting = approveRejectRewardRequest.action === actionTypes.Reject;
  const isRejectingApprovedRequest =
    isRejecting &&
    approveRejectRewardRequest.rewardRequest?.status === "Approved";

  return (
    <div>
      <div className="flex flex-justify-space flex-align-center mb-8">
        <p className="xxl-text fw-500">Reward Requests</p>
        <RewardRequestsTableActions
          eduProgram={eduProgram}
          getRequestsForExport={getRequestsForExport}
        />
      </div>
      <div className="courses-table">
        <PaginatedTable
          filterComponent={
            <RewardRequestsTableFilters
              applyFilters={filterRewardRequests}
              applySort={applySort}
              eduChabadHouses={eduChabadHouses}
              eduRewardRequestStatuses={eduRewardRequestStatuses}
              eduRewardTypes={eduRewardTypes}
              sortBy={sortBy}
              sortOptions={[
                { id: "createdOn", name: "Request Rec'vd" },
                { id: "studentName", name: "Student Name" },
              ]}
            />
          }
          loading={loading}
          loadData={loadData}
          mobileMode={mobileMode}
          page={page}
          records={rewardRequests}
          renderHeader={() => <RewardRequestsTableHeader />}
          renderRow={(request, index) => (
            <RewardRequestsTableRow
              eduProgramScheduleId={eduProgram.programScheduleID}
              key={index}
              request={request}
              onApproveRejectRewardRequest={toggleApproveRejectRewardRequest}
              viewOnly={viewOnly}
            />
          )}
          results={results}
          showResultsCount={true}
          success={success}
          totalCount={totalRewardRequests}
        />
      </div>

      {showApproveRejectRewardRequestConfirmationModal && (
        <ApproveRejectRewardRequestModal
          approveRejectRewardRequest={approveRejectRewardRequest}
          onCancel={toggleApproveRejectRewardRequest}
          onConfirm={doApproveRejectRewardRequest}
          isApproving={isApproving}
          isRejecting={isRejecting}
          isRejectingApprovedRequest={isRejectingApprovedRequest}
        />
      )}
    </div>
  );
}
